import { type KnowledgeCard } from '@/models/knowledge-card.model';

export interface AssetLibraryFilters {
  query: string | null;
  sourceType: string | null;
  tag: string | null;
}

export interface AssetLibraryListQuery {
  project_id?: string | null;
  source_type?: string;
  tag?: string;
  q?: string;
  page: number;
  page_size: number;
}

export interface AssetLibraryTagOption {
  label: string;
  value: string;
}

export interface AssetTagRecord {
  tag_name: string;
}

export type AssetOpenTarget =
  { kind: 'editor'; cardId: string } | { kind: 'external'; url: string } | { kind: 'none' };

const DATE_FORMATTER = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

/** Return the trimmed string, or undefined when the value is empty or null. */
const optionalTrimmed = (value: string | null): string | undefined => {
  const trimmed = value?.trim();
  return trimmed || undefined;
};

/**
 * Build the API list-query object from active UI filters, page, and page size.
 */
export const buildAssetLibraryQuery = (
  projectId: string | undefined,
  filters: Readonly<AssetLibraryFilters>,
  page: number,
  pageSize: number,
): AssetLibraryListQuery => ({
  project_id: projectId ?? null,
  ...(optionalTrimmed(filters.query) ? { q: optionalTrimmed(filters.query) } : {}),
  ...(filters.sourceType ? { source_type: filters.sourceType } : {}),
  ...(optionalTrimmed(filters.tag) ? { tag: optionalTrimmed(filters.tag) } : {}),
  page,
  page_size: pageSize,
});

/** Convert raw tag records into sorted, deduplicated label/value option pairs for select inputs. */
export const buildAssetTagOptions = (tags: readonly AssetTagRecord[]): AssetLibraryTagOption[] =>
  tags
    .map((tag) => tag.tag_name.trim())
    .filter(Boolean)
    .filter((tag, index, all) => all.indexOf(tag) === index)
    .sort((left, right) => left.localeCompare(right))
    .map((tag) => ({ label: tag, value: tag }));

/** Format an asset total as a human-readable count string, e.g. "1 asset" or "3 assets". */
export const formatAssetCount = (total: number): string =>
  `${total} ${total === 1 ? 'asset' : 'assets'}`;

/** Format an ISO date string as "Mon D, YYYY"; returns the raw string on parse failure. */
export const formatAssetDate = (isoDate: string): string => {
  const date = new Date(isoDate);
  return Number.isNaN(date.getTime()) ? isoDate : DATE_FORMATTER.format(date);
};

/** Determine how a knowledge card should be opened: inline editor, external URL, or nothing. */
export const getAssetOpenTarget = (card: KnowledgeCard): AssetOpenTarget => {
  if (card.sourceType === 'note' || card.sourceType === 'document') {
    return { kind: 'editor', cardId: card.cardId };
  }
  return card.sourceUrl ? { kind: 'external', url: card.sourceUrl } : { kind: 'none' };
};

/** Return the card's canonical share URL, falling back to an in-app asset library deep-link. */
export const getAssetShareUrl = (
  card: KnowledgeCard,
  origin: string,
  clientId: string,
  projectId?: string,
): string =>
  card.sourceUrl ??
  `${origin}/clients/${clientId}/projects/${projectId ?? ''}/asset_library/${card.cardId}`;
